<template>
  <div 
    class="sm-plans__top"
    :class="{
      'lvl3': provider.client_level != 2
    }"
  >
    <div class="sm-plans__top-item">
      <span>
        <span>Current plan</span>
        <span 
          v-if="Object.keys(userPlan).length && userAccount.activated_on"
          class="sm-sys"
        >
          <template v-if="userAccount.term == 'monthly'">{{`${formatPrice(userPlan.subscription_price)}\/month`}}</template>
          <template v-else>{{`${formatPrice(userPlan.period_amount)}\/${periodAlts[userPlan.period_unit]}`}}</template>
        </span>
      </span>
      <template v-if="isCustomer && userAccount.plan_id && userAccount.activated_on">
        <div class="sm-plans__top-item-plan">
          <h4>{{userPlan.title}}</h4>
        </div>
        <a
          href="#"
          @click.prevent="$emit('change-view', 'plan')"
        >View plan</a>
      </template>
      <h4 v-else>None</h4>
    </div>
    <div
      v-if="provider.client_level == 2" 
      class="sm-plans__top-item"
    >
      <span>Credit balance</span>
      <h4>{{formatPrice(balance)}}</h4>
      <div>
        <a
          v-if="fromAdmin"
          href="#"
          @click.prevent="$emit('adjust-balance')"
        >Add account credit</a>
        <a
          href="#"
          :class="{
            'disabled': !hasBalanceHistory
          }"
          @click.prevent="$emit('change-view', 'history')"
        >History</a>
      </div>
    </div>
    <div class="sm-plans__top-item">
      <span>
        <span v-if="userAccount.billing_start_date">Billing {{$moment(this.userAccount.billing_start_date) >= $moment() ? 'starting' : 'started'}} ({{$moment(this.userAccount.billing_start_date).format('MMM DD, YYYY')}})</span>
        <span v-else >Next invoice</span>
        <!-- <span v-if="userPlan.nextInvoice.date">({{userPlan.nextInvoice.date}})</span> -->
      </span>
      <h4>{{nextInvoiceText}}</h4>
      <div
        v-if="isCustomer"
      >
        <a
          href="#" 
          @click.prevent="openBillingInfo"
        >Billing information</a>
        <span v-if="provider.client_level == 2" class="disabled" style="margin-top: -4px" >|</span>
        <a
          v-if="(provider.client_level == 3 || provider.client_level == 2) && fromAdmin"
          href="#" 
          @click.prevent="openBillingOptions"
        >Billing options</a>
      </div>
    </div>

    <div
      v-if="provider.client_level == 1 || !isCustomer" 
      class="sm-plans__top-item disabled"
    >
      <span>Customer Loyalty</span>
      <h4>
        <span>Not applied</span>
      </h4>
    </div>
    <div
      v-else 
      class="sm-plans__top-item"
    >
      <span>Customer Loyalty</span>
      <template v-if="
        (userAccount.trial_end_date && new Date(userAccount.trial_end_date) > new Date()) ||
        (userAccount.coupon_code)
      ">
        <h4 v-if="userAccount.trial_end_date && new Date(userAccount.trial_end_date) > new Date()">
          <span>Trial until {{userAccount.trial_end_date | moment('D MMM, YYYY')}}</span>
        </h4>
        <h4 v-if="userAccount.coupon_code">
          <span>Coupon: {{userAccount.coupon_code}}</span>
        </h4>
      </template>
      <h4 v-else>
        <span>Not applied</span>
      </h4>

      <div v-if="fromAdmin">
        <a 
          v-if="!userAccount.trial_end_date || new Date(userAccount.trial_end_date) < new Date()"
          href="#" 
          :class="provider.client_level == 2 ? 'disabled' : ''"
          @click.prevent="openTrial"
        >Add a trial</a>
        <a
          v-else
          href="#"
          class="sm-error"
          @click.prevent="removeTrial"
        >Remove trial</a>

        <a 
          v-if="!userAccount.coupon_code"
          href="#" 
          @click.prevent="openCoupons"
        >Apply coupon</a>
        <a 
          v-else
          href="#" 
          class="sm-error"
          @click.prevent="openDeleteCoupon"
        >Delete coupon</a>
      </div>
    </div>

    <popup-block
      v-if="popup.isOpen"
      :open="popup.isOpen"
      :class="'sm-plans__top-popup'"
      :size="popup.type == 'trial' ? 'large' : ''"
      @close="popup.isOpen = false;"
    >
      <template slot="popupContent">
        <div 
          v-if="popup.type == 'gateway'"
          class="sm-plans__popup"
        >
          <h4>The operator have not set up a payment gateway yet.</h4>
          <btn-block
            :type="'small'"
            :text="'Ok'"
            @click="popup.isOpen = false"
          />
        </div>
        <billing-trial
          v-else-if="popup.type == 'trial'"
          @trial="addTrial"
        />
        <billing-coupons
          v-else-if="popup.type == 'coupons'"
          :coupons="filteredCoupons"
          @select="applyCoupon"
          @close="popup.isOpen = false"
        />
        <confirm-block
          v-else-if="popup.type == 'cancel-coupon'"
          :text="'Current coupon will be removed'"
          @confirm="cancelCoupon"
          @cancel="popup.isOpen = false"
        />
        <confirm-block
          v-else-if="popup.type == 'trial-confirm'"
          :text="`Set trial period until ${$moment(trialEndDate).format('D MMM, YYYY')}?`"
          @confirm="confirmTrial"
          @cancel="popup.isOpen = false"
        />
        <confirm-block
          v-else-if="popup.type == 'trial-remove-confirm'"
          :text="`Trial period will be ended`"
          :confirmType="'error'"
          @confirm="confirmRemoveTrial"
          @cancel="popup.isOpen = false"
        />
      </template>
    </popup-block>

    <sm-spinner :show="showSpinner" />
  </div>
</template>

<script>
import {FormatPrice} from '@/utils/general-data';
import Periods from '@/settings/periods';
import { mapActions, mapGetters } from 'vuex';
import Popup from '@/components/Popup';
import BillingTrial from '@/components/account/BillingTrial';
import BillingCoupons from '@/components/account/BillingCoupons';
import Confirm from '@/components/Confirm';
import Spinner from '@/components/Spinner';

export default {
  name: 'billing-top',
  components: {
    'popup-block': Popup,
    'billing-trial': BillingTrial,
    'billing-coupons': BillingCoupons,
    'confirm-block': Confirm,
    'sm-spinner': Spinner
  },
  props: {
    userAccount: {
      type: Object,
      required: true
    },
    userPlan: {
      type: Object,
      required: true
    },
    creditCards: {
      type: Array,
      required: true
    },
    isCustomer: {
      type: Boolean,
      default: true
    },
    fromAdmin: {
      type: Boolean,
      default: false
    },
    balance: {
      default: 0
    },
    hasBalanceHistory: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      periodAlts: Periods.alts,
      coupons: [],
      popup: {
        isOpen: false,
        type: '' // values: gateway, trial, trial-confirm, trial-remove-confirm, coupons, cancel-coupon
      },
      showSpinner: false,
      trialEndDate: ''
    }
  },
  computed: {
    ...mapGetters({
      provider: 'provider'
    }),
    hasCreditCard() {
      return !!this.creditCards.length;
    },
    nextInvoiceText() {
      if (
        !this.isCustomer ||
        !this.userAccount.activated_on ||
        !this.hasCreditCard
      )
        return 'None';

      return this.userAccount.term == 'monthly' ? this.formatPrice(this.userPlan.subscription_price) : this.formatPrice(this.userPlan.period_amount);
    },
    filteredCoupons() {
      return this.coupons.filter(item => {
        return (
          item.active && 
          (
            !item.expiration_date ||
            new Date(item.expiration_date) > new Date()
          ) &&
          (
            !item.plan_id ||
            item.plan_id == this.userAccount.plan_id
          )
        )
      });
    }
  },
  created() {
    if (this.fromAdmin) {
      this.showSpinner = true;
      this.getCoupons()
        .then(res => {
          this.coupons = res;
          this.showSpinner = false;
        });
    }
  },
  methods: {
    ...mapActions({
      getCoupons: 'billing/getCoupons',
      setCustomerCoupon: 'customers/setCustomerCoupon',
      removeCustomerCoupon: 'customers/removeCustomerCoupon',
      setCustomerTrial: 'customers/setCustomerTrial',
      removeCustomerTrial: 'customers/removeCustomerTrial',
      getCustomer: 'customers/getCustomer'
    }),
    formatPrice: FormatPrice,
    openBillingInfo() {
      if (this.provider.payment_gateway) {
        this.$emit('change-view', 'billing-info');
      } else {
        this.popup.type = 'gateway';
        this.popup.isOpen = true;
      }
    },
    openBillingOptions() {
      if (this.provider.payment_gateway) {
        this.$emit('change-view', 'billing-options');
      } else {
        this.popup.type = 'gateway';
        this.popup.isOpen = true;
      }
    },
    openTrial() {
      this.popup.type = 'trial';
      this.popup.isOpen = true;
    },
    addTrial(date) {
      this.trialEndDate = date;
      this.popup.type = 'trial-confirm';
      this.popup.isOpen = true;
    },
    confirmTrial() {
      this.showSpinner = true;
      this.popup.isOpen = false;

      this.setCustomerTrial({
        id: this.userAccount.id,
        date: this.trialEndDate
      })
        .then(res => {
          this.showSpinner = false;
        })
        .catch(e => {
          this.getCustomer(this.userAccount.id)
            .then(res => {
              this.showSpinner = false;
            })
        })
    },
    removeTrial() {
      this.popup.type = 'trial-remove-confirm';
      this.popup.isOpen = true;
    },
    confirmRemoveTrial() {
      this.showSpinner = true;
      this.popup.isOpen = false;

      this.removeCustomerTrial(this.userAccount.id)
        .then(res => {
          this.showSpinner = false;
        })
        .catch(e => {
          this.getCustomer(this.userAccount.id)
            .then(res => {
              this.showSpinner = false;
            })
        })
    },
    openCoupons() {
      this.popup.type = 'coupons';
      this.popup.isOpen = true;
    },
    openDeleteCoupon() {
      this.popup.type = 'cancel-coupon';
      this.popup.isOpen = true;
    },
    cancelCoupon() {
      this.showSpinner = true;
      this.removeCustomerCoupon(this.userAccount.id)
        .then(res => {
          this.popup.isOpen = false;
          this.showSpinner = false;
        });
    },
    applyCoupon(data) {
      this.showSpinner = true;
      this.setCustomerCoupon({
        id: this.userAccount.id,
        ...data
      })
        .then(res => {
          this.popup.isOpen = false;
          this.showSpinner = false;
        });
    }
  }
}
</script>